/**
 * Human labels for the permit types, mirroring the server's PERMIT_TYPE_LABEL
 * (api/src/tenants/seat_caps.ts).
 *
 * ONE client-side copy, imported by every screen that names a permit type —
 * Parkers' roster/capacity rows and Permits' per-type cards. Two local copies
 * is how "Vendor" on one screen becomes "vendor" on the next for the same
 * bucket, which reads as two different things to a manager.
 *
 * A type absent here still renders — its raw key — rather than vanishing: a
 * missing label is a cosmetic gap, but a missing CAPACITY ROW would hide a
 * bucket the manager is being limited by.
 */
export const TYPE_LABEL: Record<string, string> = {
  employee: 'Employee', tenant: 'Tenant', vendor: 'Vendor', monthly: 'Monthly', other: 'Other',
}

export const typeLabel = (t: string) => TYPE_LABEL[t] ?? t

/**
 * The bucket an untyped permit counts against — matches the server's
 * DEFAULT_REGISTRATION_TYPE. A permit written before per-type caps existed has
 * no `permitType`, and every reader must fall back to this rather than inventing
 * an "untyped" bucket the caps don't govern.
 */
export const DEFAULT_PERMIT_TYPE = 'employee'

/**
 * How a per-type seat count reads next to its cap — shared so Parkers' capacity
 * rows and Permits' capacity cards state the same fact in the same words. "Full"
 * is called out explicitly because that is the state that stops a manager from
 * approving anyone, and it should not have to be inferred from two equal numbers.
 */
export function capacityLabel(used: number, cap: number): string {
  return used >= cap ? `Full — ${used} of ${cap} issued` : `${used} of ${cap} issued`
}

/** What a type's queue means for the manager, or null when there's nothing to say. */
export interface TypePressure { pending: number; left: number; short: number }

/**
 * The one sentence describing a permit type's approval queue — shared so
 * Parkers' capacity rows and Permits' capacity cards can't word it differently,
 * the same rule `capacityLabel` follows.
 *
 * Returns null when there is genuinely nothing to report (an empty queue), so
 * the caller renders NO line rather than "0 requests waiting", which is noise
 * on every screen of every tenant who is up to date.
 *
 * ⚠️ `short` leads when it exists. "2 requests waiting" and "2 waiting · 1 more
 * than you have room for" prompt different actions — the first is a click, the
 * second is a conversation with the operator — and a manager who only discovers
 * the second on their SECOND approval has already told somebody they can park.
 */
export function pendingLabel(p: TypePressure | undefined): string | null {
  if (!p || p.pending === 0) return null
  if (p.short > 0) return `${p.pending} waiting · ${p.short} more than you have room for`
  return `${p.pending} ${p.pending === 1 ? 'request' : 'requests'} waiting`
}
